"use client";

import { downloadCertificate, getDayOfWeek } from "@/components/ui/games/GameCertificate";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { useToast } from "@/components/providers/FetchToastProvider";
import { Check, Copy, Download } from "lucide-react";
import { useState } from "react";
import { MAX_GUESSES } from "../_logic/constants";
import { formatDuration } from "../_logic/scoring";
import type { LeaderboardEntry, RoundState, ScoredGuess } from "../_logic/types";
import styles from "./wordle.module.css";

interface WordleResultCardProps {
  round: {
    name: string;
    state: RoundState;
    guesses: ScoredGuess[];
    answer: string | null;
    timeSeconds: number | null;
    counted: boolean;
  };
  standing: LeaderboardEntry | null;
  onPlayAsSomeoneElse: () => void;
}

const WIN_HEADLINES = ["Genius", "Magnifique", "Impressive", "Splendid", "Great", "Phew"];
const EMOJI = { correct: "🟩", present: "🟨", absent: "⬜" } as const;

function shareText(guesses: ScoredGuess[], won: boolean, seconds: number): string {
  const date = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const grid = guesses.map((g) => g.statuses.map((s) => EMOJI[s]).join("")).join("\n");
  return `Nowadays Wordle ${date} ${won ? guesses.length : "X"}/${MAX_GUESSES} in ${formatDuration(seconds)}\n\n${grid}\n\nnowadays.ai/games/wordle`;
}

/**
 * Post-round summary: headline, stats, leaderboard standing, share and certificate.
 * @param props The finished round, the player's leaderboard entry (if counted), and a reset action.
 * @returns The result card.
 */
export function WordleResultCard({ round, standing, onPlayAsSomeoneElse }: WordleResultCardProps) {
  // Hooks
  const { showToast } = useToast();

  // States
  const [copied, setCopied] = useState(false);

  // Derived values
  const won = round.state === "won";
  const seconds = round.timeSeconds ?? 0;
  const headline = won ? WIN_HEADLINES[round.guesses.length - 1] : "So close";

  // Handlers
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(shareText(round.guesses, won, seconds));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("Couldn't copy to your clipboard", "error");
    }
  };

  const handleCertificate = () =>
    downloadCertificate(
      {
        gameName: "Nowadays Wordle",
        playerName: round.name,
        stats: [getDayOfWeek(), `${round.guesses.length} attempts`, formatDuration(seconds)],
        subtitle: "Wordle champion",
      },
      `wordle-${round.name.toLowerCase().replace(/\s+/g, "-")}.png`
    );

  // Render
  return (
    <div className={`flex flex-col gap-5 ${styles.fadeIn}`}>
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold text-slate-900">{headline}</h2>
        {won ? (
          <p className="text-sm font-medium text-slate-600">
            You got <span className="font-semibold tracking-wide text-slate-900">{round.answer}</span> in{" "}
            {round.guesses.length} {round.guesses.length === 1 ? "guess" : "guesses"}.
          </p>
        ) : (
          <p className="text-sm font-medium text-slate-600">
            Today&apos;s word was <span className="font-semibold tracking-wide text-slate-900">{round.answer}</span>.
            Come back tomorrow for the next one.
          </p>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Guesses" value={won ? `${round.guesses.length}/${MAX_GUESSES}` : `X/${MAX_GUESSES}`} />
        <Stat label="Time" value={formatDuration(seconds)} />
        <Stat label="Points" value={standing ? String(standing.points) : round.counted ? "…" : "–"} />
        <Stat label="Rank" value={standing?.rank ? `#${standing.rank}` : "–"} />
      </dl>

      {!round.counted && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500">
          Your first round today is already on the leaderboard, so this one is just for fun.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <FetchButton
          variant="primary"
          onClick={handleShare}
          leftIcon={copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        >
          {copied ? "Copied" : "Copy results"}
        </FetchButton>
        {won && (
          <FetchButton variant="outline" onClick={handleCertificate} leftIcon={<Download className="h-4 w-4" />}>
            Certificate
          </FetchButton>
        )}
        <FetchButton variant="ghost" onClick={onPlayAsSomeoneElse}>
          Not {round.name.split(" ")[0]}?
        </FetchButton>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-xl bg-slate-50 px-3 py-2.5">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-slate-900">{value}</dd>
    </div>
  );
}
